import { createContext, useCallback, useState } from 'react';
import BenefitModal from '../pages/widgets/benefit/BenefitModal';
import BenefitOptionsAndCostsModal from '../pages/widgets/BenefitOptionsAndCostsModal/BenetitOptionsAndCostsModal';
import CommentModal from '../components/Case/CommentModal';
import EOICompleteModal from '../components/EOICompleteModal/EOICompleteModal';
import ViewCaseFormModal from '../components/Case/ViewCaseFormModal';
import IDCardModal from '../components/IDCardModal/IDCardModal';
import PaymentInformationStatementModal from '../components/PaymentInformationStatementModal/PaymentInformationStatementModal';
import PaymentScheduleModal from '../components/PaymentScheduleModal/PaymentScheduleModal';
import PensionVerificationLetter from '../pages/widgets/pensionVerificationLetter/pensionVerificationLetter';
import PensionVerificationLetterSuccessModal from '../pages/widgets/PensionVerificationLetterModal';
import UploadDocModal from '../components/UploadDocForm/UploadDocModal';
import UploadSuccessModal from '../components/UploadSuccessModal/UploadSuccessModal';
import VerificationCoverageModal from '../pages/widgets/VerificationCoverageModal/VerificationCoverageModal';
import ViewFamilyMembersModal from '../pages/widgets/healthBenefits/ViewFamilyMembersModal';

export type ModalManagementState = {
  openModal: (mType: string, params?: any) => void;
};

export const ModalManagementStore = createContext<ModalManagementState>({
  openModal: (mType: string, params?: any) => {},
});

const { Provider } = ModalManagementStore;

enum ModalTypes {
  BenefitInfo = 'benefit-info',
  BenefitOptionsAndCosts = 'benefit-options-and-costs',
  CommentCase = 'comment-case',
  DocumentUpload = 'document-upload',
  DocumentUploadSuccess = 'document-upload-success',
  EOICompleteModal = 'eoi-complete-modal',
  ViewCaseModal = 'view-case',
  IDCard = 'id-card',
  PaymentInformationStatement = 'payment-information-statement',
  PaymentSchedule = 'payment-schedule',
  PensionVerificationLetter = 'pension-verification-letter',
  PensionVerificationLetterSuccess = 'pension-verification-letter-success',
  VerificationOfCoverage = 'verification-of-coverage',
  ViewFamilyMembers = 'view-family-members',
}

const defaultDateConfig = {
  month: new Date().getMonth(),
  year: new Date().getFullYear(),
};

export const ModalManagementProvider = ({ children }: any) => {
  const [modalType, setModalType] = useState<string | undefined>(undefined);

  const [params, setParams] = useState<any>({});
  const shouldModalBeShown = useCallback(
    (currentModal: ModalTypes) => {
      return currentModal === modalType;
    },
    [modalType]
  );

  const handleCloseModal = useCallback(() => {
    setModalType(undefined);
  }, []);

  const openModal = (mType: string, params?: any) => {
    setModalType(mType);
    setParams(params);
  };

  return (
    <Provider value={{ openModal }}>
      {children}
      <PensionVerificationLetter
        showModal={shouldModalBeShown(ModalTypes.PensionVerificationLetter)}
        handleCloseModal={handleCloseModal}
        handleConfirm={() => {
          openModal(ModalTypes.PensionVerificationLetterSuccess);
          // handleCloseModal();
        }}
      />
      <PaymentInformationStatementModal
        showModal={shouldModalBeShown(ModalTypes.PaymentInformationStatement)}
        handleClose={handleCloseModal}
        dateConfig={params?.dateConfig || defaultDateConfig}
      />

      <PaymentScheduleModal
        showModal={shouldModalBeShown(ModalTypes.PaymentSchedule)}
        handleClose={handleCloseModal}
      />

      <PensionVerificationLetterSuccessModal
        showModal={shouldModalBeShown(
          ModalTypes.PensionVerificationLetterSuccess
        )}
        handleClose={handleCloseModal}
      />
      <UploadDocModal
        showModal={shouldModalBeShown(ModalTypes.DocumentUpload)}
        handleClose={handleCloseModal}
        handleSubmit={() => {
          openModal(ModalTypes.DocumentUploadSuccess, {
            successMessage: (
              <p className="m-0 fw-light text-fs-6  text-center lh-xs ">
                <p className={'w-100 p-0 m-0'}>
                  Document Uploaded Successfully!
                </p>
                It takes about an hour to process your upload, so please check
                the document center in about an hour.
              </p>
            ),
          });
        }}
      />

      <UploadSuccessModal
        showModal={shouldModalBeShown(ModalTypes.DocumentUploadSuccess)}
        handleClose={handleCloseModal}
        successMessage={params?.successMessage}
      />

      <IDCardModal
        showModal={shouldModalBeShown(ModalTypes.IDCard)}
        handleClose={handleCloseModal}
      />

      <CommentModal
        showModal={shouldModalBeShown(ModalTypes.CommentCase)}
        handleClose={handleCloseModal} 
        caseId={params?.caseId}
      />

      <ViewCaseFormModal
        showModal={shouldModalBeShown(ModalTypes.ViewCaseModal)}
        handleClose={handleCloseModal}
        handleSubmit={handleCloseModal}
        caseId={params?.caseId}
      />

      <EOICompleteModal
        showModal={shouldModalBeShown(ModalTypes.EOICompleteModal)}
        handleClose={handleCloseModal}
      />

      <VerificationCoverageModal
        showVerificationCoverageModal={shouldModalBeShown(
          ModalTypes.VerificationOfCoverage
        )}
        handleCloseVerificationCoverageModal={handleCloseModal}
      />
      <BenefitModal
        showBenefitModal={shouldModalBeShown(ModalTypes.BenefitInfo)}
        handleCloseBenefitModal={handleCloseModal}
      />
      <BenefitOptionsAndCostsModal
        showBenefitModal={shouldModalBeShown(ModalTypes.BenefitOptionsAndCosts)}
        handleCloseBenefitModal={handleCloseModal}
        healthBenefits={params?.healthBenefits}
        spendingAccounts={params?.spendingAccounts}
        lifeAndDisabilityInsurances={params?.lifeAndDisabilityInsurances}
        lifestyleBenefits={params?.lifestyleBenefits}
      />
      <ViewFamilyMembersModal
        showModal={shouldModalBeShown(ModalTypes.ViewFamilyMembers)}
        handleCloseModal={handleCloseModal}
        addFamilyMember={params?.addFamilyMember || []}
        familyMembers={params?.familyMembers || []}
      />
    </Provider>
  );
};
